import { useState, useMemo } from 'react'
import { X, Eye, EyeOff, CheckCircle, AlertCircle, Check, Lock, Mail, Building2, Package, Shield } from 'lucide-react'
import Button from '../UI/Button'
import Input from '../UI/Input'

const RegistrationModal = ({ isOpen, onClose, invitationData, token, onSuccess, embedded = false, hideSummary = false, submitClassName = '' }) => {
  // Store productLoginUrl from response
  const [productLoginUrl, setProductLoginUrl] = useState(null)
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [success, setSuccess] = useState(false)

  // Common weak passwords
  const weakPasswords = [
    'password', 'password123', 'password1',
    '123456', '12345678', '123456789', '1234567890'
  ]

  // Check individual password requirements
  const checkPasswordRequirements = (password) => {
    const email = invitationData?.invitation?.email || invitationData?.email || ''
    const emailUsername = email ? email.split('@')[0].toLowerCase() : ''
    const lowerPassword = password.toLowerCase()

    return {
      length: password.length >= 8 && password.length <= 12,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      specialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      notWeak: !weakPasswords.some(weak => lowerPassword === weak || lowerPassword.includes(weak)),
      notEmail: !email || (!lowerPassword.includes(emailUsername) && !lowerPassword.includes(email.toLowerCase()))
    }
  }

  const validatePassword = (password) => {
    const errors = []
    const requirements = checkPasswordRequirements(password)

    // Check length (8-12 characters)
    if (!requirements.length) {
      if (password.length < 8) {
        errors.push('Password must be at least 8 characters')
      } else if (password.length > 12) {
        errors.push('Password must be no more than 12 characters')
      }
    }

    // Check for uppercase letter
    if (!requirements.uppercase) {
      errors.push('Password must contain at least 1 uppercase letter')
    }

    // Check for lowercase letter
    if (!requirements.lowercase) {
      errors.push('Password must contain at least 1 lowercase letter')
    }

    // Check for number
    if (!requirements.number) {
      errors.push('Password must contain at least 1 number')
    }

    // Check for special character
    if (!requirements.specialChar) {
      errors.push('Password must contain at least 1 special character')
    }

    // Check for common/weak passwords (case-insensitive)
    if (!requirements.notWeak) {
      errors.push('Password is too common or weak. Please choose a stronger password')
    }

    // Check if password contains email or username
    if (!requirements.notEmail) {
      errors.push('Password cannot contain your email or username')
    }

    return errors
  }

  // Get password requirements status for real-time feedback
  const passwordRequirements = useMemo(() => {
    if (!formData.password) return null
    return checkPasswordRequirements(formData.password)
  }, [formData.password, invitationData])

  // Check if all requirements are met
  const allRequirementsMet = useMemo(() => {
    if (!passwordRequirements) return false
    return Object.values(passwordRequirements).every(req => req === true)
  }, [passwordRequirements])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else {
      const passwordErrors = validatePassword(formData.password)
      if (passwordErrors.length > 0) {
        // Show the first error, or combine them
        newErrors.password = passwordErrors[0]
      }
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsLoading(true)
    setErrors({})

    try {
      const rawBase = import.meta.env.VITE_API_URL || 'http://localhost:5000'
      const normalizedBase = rawBase.endsWith('/api')
        ? rawBase.replace(/\/$/, '')
        : `${rawBase.replace(/\/$/, '')}/api`
      
      // Use the same invitation endpoint as the organization invite flow
      const endpoint = `${normalizedBase}/invitations/accept/${token}`
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          password: formData.password,
          confirm_password: formData.confirmPassword
        })
      })

      const data = await response.json()

      if (data.success) {
        setSuccess(true)
        // Store product login URL if available
        const loginUrl = data.data?.productLoginUrl || null
        setProductLoginUrl(loginUrl)
        
        setTimeout(() => {
          // Pass productLoginUrl to onSuccess callback
          onSuccess(data.data.user, loginUrl)
          onClose()
        }, 2000)
      } else {
        setErrors({ submit: data.message || 'Registration failed' })
      }
    } catch (error) {
      console.error('Registration error:', error)
      setErrors({ submit: 'Network error. Please try again.' })
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  const inviteEmail = invitationData?.invitation?.email || invitationData?.email || ''
  const isHubUser = Boolean(invitationData?.isHubUser)
  const orgName = isHubUser
    ? 'Product Hub'
    : invitationData?.organization?.name || 'your organization'
  const products = Array.isArray(invitationData?.products) ? invitationData.products : []
  const productNames = products.map((item) => item?.name).filter(Boolean)
  const productLabel = productNames.length ? productNames.join(', ') : ''
  const roleName =
    invitationData?.product_role?.name ||
    invitationData?.role?.name ||
    invitationData?.invitation?.role?.name
  const requirementRows = passwordRequirements
    ? [
        [passwordRequirements.length, '8-12 characters'],
        [passwordRequirements.uppercase, 'At least 1 uppercase letter'],
        [passwordRequirements.lowercase, 'At least 1 lowercase letter'],
        [passwordRequirements.number, 'At least 1 number'],
        [passwordRequirements.specialChar, 'At least 1 special character'],
        [passwordRequirements.notWeak, 'Not a common/weak password'],
        [passwordRequirements.notEmail, 'Does not contain email or username']
      ]
    : []

  const formContent = success ? (
    <div className="flex gap-3 rounded-2xl border border-primary-200 bg-primary-50 p-4">
      <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-primary-600" />
      <div>
        <p className="text-sm font-medium text-primary-900">Account created</p>
        <p className="mt-1 text-sm text-primary-700">
          {productLoginUrl
            ? 'Taking you to your product login...'
            : 'Redirecting you to sign in...'}
        </p>
      </div>
    </div>
  ) : (
    <form onSubmit={handleSubmit} className="space-y-5">
      {invitationData && !hideSummary && (
        <div className="rounded-2xl border border-secondary-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-secondary-400">
            {isHubUser ? 'Hub invitation' : 'Product invitation'}
          </p>
          <p className="mt-1.5 text-sm font-semibold text-secondary-900">
            {isHubUser ? 'Join Product Hub' : `Join ${productLabel || orgName}`}
          </p>
          <div className="mt-3 space-y-2">
            {inviteEmail && (
              <div className="flex items-center gap-2.5 text-sm text-secondary-700">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <Mail className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                  <span className="block text-[11px] uppercase tracking-wide text-secondary-400">Email</span>
                  <span className="block truncate font-medium">{inviteEmail}</span>
                </span>
              </div>
            )}
            {!isHubUser && productLabel && (
              <div className="flex items-center gap-2.5 text-sm text-secondary-700">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                  <Package className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                  <span className="block text-[11px] uppercase tracking-wide text-secondary-400">Product</span>
                  <span className="block font-medium">{productLabel}</span>
                </span>
              </div>
            )}
            <div className="flex items-center gap-2.5 text-sm text-secondary-700">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                <Building2 className="h-4 w-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-[11px] uppercase tracking-wide text-secondary-400">
                  {isHubUser ? 'Workspace' : 'Organization'}
                </span>
                <span className="block font-medium">{orgName}</span>
              </span>
            </div>
            {roleName && (
              <div className="flex items-center gap-2.5 text-sm text-secondary-700">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                  <Shield className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                  <span className="block text-[11px] uppercase tracking-wide text-secondary-400">
                    {isHubUser ? 'Hub role' : 'Product role'}
                  </span>
                  <span className="block font-medium">{roleName}</span>
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      <div>
        <label htmlFor="password" className="auth-field">
          Password
        </label>
        <div className="relative">
          <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400" />
          <Input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            autoFocus
            required
            placeholder="Create a password"
            value={formData.password}
            onChange={handleInputChange}
            className={`auth-input w-full pl-11 pr-11 ${errors.password ? 'border-error-500' : ''}`}
            disabled={isLoading}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-secondary-400 hover:text-secondary-600"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {errors.password && (
          <p className="mt-2 flex items-start gap-1.5 text-sm text-error-600">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            {errors.password}
          </p>
        )}
        {formData.password && passwordRequirements && !allRequirementsMet && (
          <div className="mt-3 grid grid-cols-2 gap-1.5">
            {requirementRows.map(([ok, label]) => (
              <span
                key={label}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium ${
                  ok ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                }`}
              >
                {ok ? <Check className="h-3 w-3" /> : <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />}
                {label}
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <label htmlFor="confirmPassword" className="auth-field">
          Confirm password
        </label>
        <div className="relative">
          <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400" />
          <Input
            id="confirmPassword"
            name="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            autoComplete="new-password"
            required
            placeholder="Confirm your password"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            className={`auth-input w-full pl-11 pr-11 ${errors.confirmPassword ? 'border-error-500' : ''}`}
            disabled={isLoading}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-secondary-400 hover:text-secondary-600"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
          >
            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {errors.confirmPassword && (
          <p className="mt-2 flex items-start gap-1.5 text-sm text-error-600">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            {errors.confirmPassword}
          </p>
        )}
      </div>

      {errors.submit && (
        <div className="flex items-start gap-2 rounded-xl border border-error-200 bg-error-50 px-3 py-2.5">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-error-500" />
          <p className="text-sm text-error-600">{errors.submit}</p>
        </div>
      )}

      {embedded ? (
        <Button
          type="submit"
          className={`h-12 w-full rounded-full text-[15px] font-semibold shadow-sm ${submitClassName}`}
          loading={isLoading}
          disabled={isLoading}
        >
          {isLoading ? 'Creating account...' : embedded ? 'Accept invitation' : 'Continue'}
        </Button>
      ) : (
        <div className="flex gap-3 pt-1">
          <Button type="button" variant="secondary" className="h-12 flex-1 rounded-xl" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" className="h-12 flex-1 rounded-xl font-semibold" loading={isLoading} disabled={isLoading}>
            {isLoading ? 'Creating account...' : embedded ? 'Accept invitation' : 'Continue'}
          </Button>
        </div>
      )}
    </form>
  )

  if (embedded) {
    return formContent
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-secondary-900/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md overflow-y-auto rounded-2xl border border-secondary-200 bg-secondary-50 shadow-xl">
        <div className="flex items-center justify-between border-b border-secondary-200 px-6 py-4">
          <h2 className="text-lg font-semibold tracking-tight text-secondary-900">
            Complete your registration
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-secondary-400 transition-colors hover:bg-white hover:text-secondary-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6">{formContent}</div>
      </div>
    </div>
  )
}

export default RegistrationModal
